import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'

import { View } from 'react-native'
import { VFButton, VFText } from '@vfgroup-oneplatform/foundation/Components'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import { getSelectedBundleData } from '../../Utils/bundle'

import UnlimitedContext from '../../Context'

import {
  Members,
  CardHeader,
  AddMemberForm,
  MemberPlaceholder
} from '../MembersList/MembersList'

import CustomInput from '../CustomInput/CustomInput'

import { STATUS } from '../../Utils/members'

import styles from './GroupStep.Styles'

const validate = ({ value, fn, text }) => {
  const formatError = fn(value) ? '' : text
  const message = formatError
  const isError = Boolean(formatError)
  return { message, isError }
}

const getSlotCount = val => {
  const count = val ? val.split(' ')[0] : ''
  return count ? parseInt(count, 10) : 0
}

const hasMembersLoadingOrError = members => {
  if (members.length === 0) {
    return false
  }
  return Boolean(
    members.find(member =>
      [STATUS.error, STATUS.loading].includes(member.status)
    )
  )
}

const GroupStep = ({
  goNext,
  removeMember,
  validateName,
  validateMember,
  changeGroupName,
  formatPhoneNumber,
  validatePhoneNumber
}) => {
  const {
    data: {
      groupData,
      customTVs,
      bundleType,
      atlBundles,
      pegaBundles,
      customPlans,
      customSlots,
      customSpeeds,
      selectedBundle,
      customBundlePrice,
      customBundleOptions
    }
  } = useContext(UnlimitedContext)

  const bundleData = getSelectedBundleData({
    customTVs,
    bundleType,
    atlBundles,
    pegaBundles,
    customPlans,
    customSlots,
    customSpeeds,
    selectedBundle,
    customBundlePrice,
    customBundleOptions
  })

  const handleNextClick = () => goNext()

  const maxSlots = getSlotCount(bundleData?.slots?.id)

  const theme = useTheme()

  const exceededMembers = groupData.members.length > maxSlots

  const isNextDisabled =
    exceededMembers ||
    !validateName(groupData.name) ||
    hasMembersLoadingOrError(groupData.members)

  return (
    <View style={styles.groupStep}>
      <VFText i18nKey="unlimited_my_members" style={styles.title} />
      <VFText
        style={styles.description}
        i18nKey="unlimited_create_group_description"
      />
      {!maxSlots && !exceededMembers ? (
        <>
          <View style={styles.card(theme)}>
            <GroupNameForm
              slots={maxSlots}
              validateFn={validateName}
              changeGroupName={changeGroupName}
            />
            <MemberPlaceholder text="unlimited_no_slots_selected" />
          </View>
          {/* Divider */}
          <View style={styles.divider} />
        </>
      ) : (
        <>
          <View style={styles.card(theme)}>
            <CardHeader title="unlimited_group_creation_title" />
            <View>
              <GroupNameForm
                slots={maxSlots}
                validateFn={validateName}
                changeGroupName={changeGroupName}
              />
              <MembersForm
                maxSlots={maxSlots}
                onAdd={validateMember}
                onRemove={removeMember}
                formatFn={formatPhoneNumber}
                validateFn={validatePhoneNumber}
                tariffPlan={bundleData?.tariffPlan?.id}
              />
              <VFText
                style={styles.smsHint}
                i18nKey="unlimited_creation_sms_confirmation"
              />
            </View>
          </View>
          {/* Divider */}
          <View style={styles.divider} />
        </>
      )}
      {/* Button */}
      <VFButton
        onPress={handleNextClick}
        disabled={isNextDisabled}
        title="unlimited_next_step_button"
      />
    </View>
  )
}

const MembersForm = ({
  onAdd,
  onRemove,
  formatFn,
  maxSlots,
  tariffPlan,
  validateFn
}) => {
  const {
    data: { groupData, loggedInNumber }
  } = useContext(UnlimitedContext)

  const [number, setNumber] = useState('')

  const memberCount = groupData.members.length.toString()

  const handleMemberAdd = data =>
    onAdd({ body: { number: data.number, tariffPlan } })

  const handleRemove = ({ member: data }) => {
    onRemove({ number: data.number })
  }

  const handleRetry = ({ member: data }) =>
    onAdd({ body: { number: data.number, tariffPlan, retry: true } })

  return (
    <View style={styles.members}>
      <View style={styles.flexRow}>
        <VFText i18nKey="unlimited_my_members_short" style={styles.subtitle} />
        <VFText
          style={styles.subtitle}
          i18nKey="unlimited_members_count"
          placeHolders={[memberCount, maxSlots]}
        />
      </View>
      <View style={styles.content}>
        <Members
          isGroupAdmin={true}
          onRetry={handleRetry}
          onRemove={handleRemove}
          members={groupData.members}
        />
      </View>
      {/* Divider */}
      <View style={[styles.divider, styles.marginBottom]} />
      {maxSlots === memberCount ? (
        <VFText i18nKey="unlimited_filled_slots" style={styles.explain} />
      ) : memberCount > maxSlots ? (
        <VFText i18nKey="unlimited_exceeded_slots" style={styles.explain} />
      ) : null}
      <AddMemberForm
        showAddButton
        number={number}
        formatFn={formatFn}
        onChange={setNumber}
        groupCount={maxSlots}
        onAdd={handleMemberAdd}
        validateFn={validateFn}
        members={groupData.members}
        loggedInNumber={loggedInNumber}
      />
    </View>
  )
}

const errorDefaults = {
  isError: false,
  message: ''
}

const GroupNameForm = ({ slots, changeGroupName, validateFn }) => {
  const [, setInputRef] = useState(null)

  const {
    data: { groupData }
  } = useContext(UnlimitedContext)

  const [name, setName] = useState(() => groupData.name)
  const [error, setError] = useState(errorDefaults)

  const updateGroupNameCtx = () => changeGroupName(name)

  const handleValidation = value => {
    const data = validate({
      value: value,
      fn: validateFn,
      text: 'unlimited_invalid_group_name'
    })
    setError(data)
    return data
  }

  return (
    <View style={styles.nameForm}>
      {slots ? (
        <VFText
          style={styles.nameText}
          placeHolders={[slots.toString()]}
          i18nKey="unlimited_group_name_description"
        />
      ) : null}
      <CustomInput
        showRightIcon
        value={name}
        fieldRef={setInputRef}
        onChangeText={setName}
        isError={error.isError}
        onBlur={updateGroupNameCtx}
        errorMessage={error.message}
        validator={handleValidation}
        label="unlimited_group_name_label"
        resetValidation={() => setError(errorDefaults)}
      />
    </View>
  )
}

GroupNameForm.propTypes = {
  slots: PropTypes.string,
  validateFn: PropTypes.func,
  changeGroupName: PropTypes.func
}

GroupStep.propTypes = {
  goNext: PropTypes.func,
  validateName: PropTypes.func,
  removeMember: PropTypes.func,
  validateMember: PropTypes.func,
  changeGroupName: PropTypes.func,
  formatPhoneNumber: PropTypes.func,
  validatePhoneNumber: PropTypes.func
}

MembersForm.propTypes = {
  onAdd: PropTypes.func,
  formatFn: PropTypes.func,
  onRemove: PropTypes.func,
  maxSlots: PropTypes.number,
  validateFn: PropTypes.func,
  tariffPlan: PropTypes.string
}

export default GroupStep
